
import { supabase } from '@/integrations/supabase/client';
import { ChatGroupUser } from '@/types/chat';
import { auditService } from '@/services/auditService';

export const supabaseChatGroupUsersService = {
  // Add users to a chat group
  async addUsersToGroup(chatGroupId: string, userIds: string[], currentUser?: any): Promise<void> {
    console.log('Adding users to group via Supabase:', chatGroupId, userIds, 'Current user:', currentUser);
    
    // Add users to the chat group
    const insertData = userIds.map(userId => ({
      chat_group_id: chatGroupId,
      user_id: userId
    }));

    const { error } = await supabase
      .from('chat_group_users')
      .insert(insertData);

    if (error) {
      console.error('Error adding users to group:', error);
      throw error;
    }

    // Log the action
    await auditService.logAction(
      'CREATE',
      'chat_group_users',
      chatGroupId,
      { added_users: userIds }
    );

    console.log('Users added to group successfully');
  },

  // Get users in a chat group
  async getChatGroupUsers(chatGroupId: string): Promise<ChatGroupUser[]> {
    console.log('Getting chat group users via Supabase:', chatGroupId);
    
    // First get the chat group users
    const { data: chatGroupUsersData, error: chatGroupUsersError } = await supabase
      .from('chat_group_users')
      .select('user_id, joined_at, is_active')
      .eq('chat_group_id', chatGroupId)
      .eq('is_active', true);

    if (chatGroupUsersError) {
      console.error('Error fetching chat group users:', chatGroupUsersError);
      return [];
    }

    if (!chatGroupUsersData || chatGroupUsersData.length === 0) {
      return [];
    }

    // Get the user IDs
    const userIds = chatGroupUsersData.map(item => item.user_id);

    // Fetch user profiles from Supabase profiles table
    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('id, username, first_name, last_name, role')
      .in('id', userIds);

    if (profilesError) {
      console.error('Error fetching user profiles:', profilesError);
    }

    // Also try to fetch from internal_users table for internal users
    const { data: internalUsersData, error: internalUsersError } = await supabase
      .from('internal_users')
      .select('id, username, role')
      .in('id', userIds);

    if (internalUsersError) {
      console.error('Error fetching internal users:', internalUsersError);
    }

    const users: ChatGroupUser[] = chatGroupUsersData.map(item => {
      const profile = profilesData?.find(p => p.id === item.user_id);
      const internalUser = internalUsersData?.find(u => u.id === item.user_id);
      
      // Prefer internal user data if available, then profile data
      let userDetails;
      if (internalUser) {
        userDetails = {
          id: internalUser.id,
          username: internalUser.username,
          first_name: internalUser.username,
          last_name: '',
          role: internalUser.role || 'fan'
        };
      } else if (profile) {
        userDetails = {
          id: profile.id,
          username: profile.username || `User-${item.user_id.slice(0, 8)}`,
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          role: profile.role || 'fan'
        };
      } else {
        userDetails = {
          id: item.user_id,
          username: `User-${item.user_id.slice(0, 8)}`,
          first_name: 'Unknown',
          last_name: 'User',
          role: 'fan'
        };
      }
      
      return {
        chat_group_id: chatGroupId,
        user_id: item.user_id,
        joined_at: item.joined_at,
        is_active: item.is_active,
        user_details: userDetails
      };
    });

    console.log('Fetched chat group users:', users);
    return users;
  },

  // Leave a chat group
  async leaveGroup(chatGroupId: string, userId: string): Promise<void> {
    console.log('User leaving group via Supabase:', userId, 'from group:', chatGroupId);
    
    const { error } = await supabase
      .from('chat_group_users')
      .delete()
      .eq('chat_group_id', chatGroupId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error leaving group:', error);
      throw error;
    }

    // Log the action
    await auditService.logAction(
      'DELETE',
      'chat_group_users',
      chatGroupId,
      { user_left: userId }
    );

    console.log('User left group successfully');
  }
};
